import cn from 'classnames';
// Types
import { MatchupCorner } from 'api/graphql-global-types';
// Helpers
import { formatLargeNumber } from 'helpers/formatLargeNumber';
import { withCDN } from 'helpers/cdn';
// Ui
import Image from 'ui3/Image/Image';
// Helpers
import {
  Matchup,
  MatchupCompetitor,
  MatchupResults,
  getBoutLabel,
  getCompetitorDisplayName,
  getCompetitorShortName,
  getCornerCompetitor,
  getVoteBreakdown,
} from 'helpers/streamMatchups';
// Styles
import styles from './FightCard.module.scss';

const FALLBACK_AVATAR = '/images/logos/mobile-logo-black.svg';

type BoutOutcome = 'winner' | 'loser' | null;

type CompetitorColumnProps = {
  competitor: MatchupCompetitor | null;
  outcome: BoutOutcome;
};

const CompetitorColumn = ({ competitor, outcome }: CompetitorColumnProps) => {
  const displayName = getCompetitorDisplayName(competitor);
  const audience = competitor?.store?.audience;

  return (
    <div className={styles.competitor}>
      <div
        className={cn(styles.avatar, {
          [styles.avatarWinner]: outcome === 'winner',
          [styles.avatarLoser]: outcome === 'loser',
        })}
      >
        <Image
          ssrFill
          src={
            competitor?.store?.storeDetails?.avatarURL ||
            withCDN(FALLBACK_AVATAR)
          }
          alt={displayName}
          title={displayName}
          sizes="108px"
        />
      </div>
      <div className={styles.competitorDetails}>
        {outcome === 'winner' && <p className={styles.winnerLabel}>Winner</p>}
        <p className={styles.competitorName}>{displayName}</p>
        {Boolean(audience) && (
          <p className={styles.competitorAudience}>
            {`${formatLargeNumber(audience as number)} audience`}
          </p>
        )}
      </div>
    </div>
  );
};

export type FightCardProps = {
  matchup: Matchup;
  boutNumber: number;
  results: MatchupResults | null;
  canVote: boolean;
  canShare: boolean;
  isVoting: boolean;
  onVote: (competitorId: string) => void;
  onSharePick: (matchupId: string) => void;
};

const FightCard = ({
  matchup,
  boutNumber,
  results,
  canVote,
  canShare,
  isVoting,
  onVote,
  onSharePick,
}: FightCardProps): JSX.Element => {
  const cornerA = getCornerCompetitor(matchup, MatchupCorner.A);
  const cornerB = getCornerCompetitor(matchup, MatchupCorner.B);

  const breakdown = getVoteBreakdown(
    results,
    cornerA?.id || null,
    cornerB?.id || null
  );

  const boutLabel = getBoutLabel(matchup, boutNumber);
  const cornerAName = getCompetitorDisplayName(cornerA);
  const cornerBName = getCompetitorDisplayName(cornerB);

  const outcomeFor = (corner: MatchupCorner): BoutOutcome => {
    if (!matchup.winnerCorner) {
      return null;
    }
    return matchup.winnerCorner === corner ? 'winner' : 'loser';
  };

  const showResults = breakdown.hasVoted || (!canVote && breakdown.total > 0);
  const showVoteButtons = canVote && !showResults;
  const showVoteRow = showResults || showVoteButtons;

  const voteCountLabel =
    breakdown.total === 1 ? '1 vote' : `${breakdown.total} votes`;

  const renderVoteButton = (
    competitor: MatchupCompetitor | null,
    corner: MatchupCorner
  ) => {
    if (!competitor) {
      return null;
    }

    return (
      <button
        type="button"
        className={cn(styles.voteButton, {
          [styles.voteButtonA]: corner === MatchupCorner.A,
          [styles.voteButtonB]: corner === MatchupCorner.B,
        })}
        disabled={isVoting}
        aria-label={`Vote for ${getCompetitorDisplayName(competitor)}`}
        onClick={() => onVote(competitor.id)}
      >
        {getCompetitorShortName(competitor)}
      </button>
    );
  };

  return (
    <article
      className={cn(styles.root, { [styles.mainEvent]: matchup.isMainEvent })}
    >
      <div className={styles.body}>
        <div className={styles.top}>
          <CompetitorColumn
            competitor={cornerA}
            outcome={outcomeFor(MatchupCorner.A)}
          />

          <div className={styles.middle}>
            <p
              className={cn(styles.boutLabel, {
                [styles.mainEventLabel]: matchup.isMainEvent,
              })}
            >
              {matchup.isMainEvent && (
                <img
                  src="/images/stream-tabs/trophy.svg"
                  alt=""
                  aria-hidden="true"
                  className={styles.trophyIcon}
                />
              )}
              <span className={styles.boutLabelText}>{boutLabel}</span>
            </p>
            <div className={styles.versus}>
              <img
                src="/images/stream-tabs/swords.svg"
                alt=""
                aria-hidden="true"
                className={styles.swordsIcon}
              />
              <span className={styles.versusText}>VS</span>
            </div>
          </div>

          <CompetitorColumn
            competitor={cornerB}
            outcome={outcomeFor(MatchupCorner.B)}
          />
        </div>

        {showVoteRow && (
          <div className={styles.voteSection}>
            <div className={styles.voteMeta}>
              <p className={styles.voteMetaLabel}>WHO WINS?</p>
              <p className={styles.voteMetaCount}>{voteCountLabel}</p>
            </div>

            {showResults ? (
              <div
                className={styles.resultsBar}
                role="img"
                aria-label={`${cornerAName} ${breakdown.cornerA.percentage}%, ${cornerBName} ${breakdown.cornerB.percentage}%`}
              >
                {breakdown.cornerA.percentage > 0 && (
                  <div
                    className={cn(
                      styles.resultsSegment,
                      styles.resultsSegmentA
                    )}
                    style={{ width: `${breakdown.cornerA.percentage}%` }}
                  >
                    <span className={styles.resultsSegmentLabel}>
                      {`${getCompetitorShortName(cornerA)} (${
                        breakdown.cornerA.percentage
                      }%)`}
                    </span>
                  </div>
                )}
                {breakdown.cornerB.percentage > 0 && (
                  <div
                    className={cn(
                      styles.resultsSegment,
                      styles.resultsSegmentB
                    )}
                    style={{ width: `${breakdown.cornerB.percentage}%` }}
                  >
                    <span className={styles.resultsSegmentLabel}>
                      {`${getCompetitorShortName(cornerB)} (${
                        breakdown.cornerB.percentage
                      }%)`}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div className={styles.voteButtons}>
                {renderVoteButton(cornerA, MatchupCorner.A)}
                {renderVoteButton(cornerB, MatchupCorner.B)}
              </div>
            )}
          </div>
        )}

        {breakdown.hasVoted && canShare && (
          <button
            type="button"
            className={styles.shareButton}
            onClick={() => onSharePick(matchup.id)}
          >
            <img
              src="/images/stream-tabs/share.svg"
              alt=""
              aria-hidden="true"
              className={styles.shareIcon}
            />
            <span className={styles.shareLabel}>Share your pick</span>
          </button>
        )}
      </div>
    </article>
  );
};

export default FightCard;
